import React from 'react';
import {StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {observer} from 'mobx-react-lite';

import AddToWallet from '../../components/rewards/AddToWallet';
import InStoreCode from '../../components/rewards/InStoreCode';
import MemberCard from '../../components/rewards/MemberCard';
import Button from '../../components/themed/Button';
import Text from '../../components/themed/Text';
import ContentView from '../../components/ui-controls/layout/ContentView';
import config from '../../config';
import {layout, lineHeight, resolveWeight, space, type} from '../../design-system/tokens';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import logoImg from '../../images/brand/header-logo.png';
import SignUpPageLayout, {
  CTA_HEIGHT,
  CTA_RADIUS,
  useWideLayout,
} from '../../screens/account/signUp/SignUpPageLayout';
import theme from '../../style/theme';

type RequireAll<TOptional, TRequired> =
  | (TOptional & TRequired)
  | (Partial<Record<keyof TOptional, never>> & TRequired);

type SuccessLoyaltyProps = RequireAll<
  {
    inStoreCode: string;
    fullName: string;
    cardNumber: string;
    walletUrl: string;
  },
  {
    goToHome: () => void;
    title: string;
    text: string | React.ReactNode;
    pending?: boolean;
    ctaTitle?: string;
    memberSince?: string;
    isStaff?: boolean;
    isLmgStaff?: boolean;
  }
>;

/**
 * Success screen with loyalty card
 */
const SuccessLoyalty = observer((props: SuccessLoyaltyProps) => {
  const {
    title,
    text,
    inStoreCode,
    walletUrl,
    cardNumber,
    fullName,
    pending,
    goToHome,
    ctaTitle,
    memberSince,
    isStaff,
    isLmgStaff,
  } = props;
  // DIS-1763: on web mobile the tab bar floats over the content, so the CTA
  // needs room below it to scroll clear.
  const tabBarPadding = useTabBarBottomPadding();
  const {isDesktopCard: isWide, onLayout} = useWideLayout();

  const memberCard = (
    <MemberCard
      pending={pending}
      logoImg={logoImg}
      cardNumber={cardNumber || ''}
      name={fullName || ''}
      memberSince={memberSince}
      isStaff={isStaff}
      isLmgStaff={isLmgStaff}
    />
  );

  // DIS-1842: was fontWeight 600, which Android renders Regular.
  const tempCode = inStoreCode ? (
    <>
      <Text
        style={[resolveWeight('semiBold'), isWide ? styles.tempHeadingWide : styles.tempHeading]}
        variant="heading2">
        Your temporary rewards code
      </Text>
      <Text variant="body2" style={isWide ? styles.tempBodyWide : styles.tempBody}>
        Want to keep ahold of your phone? Give this code at checkout instead.
      </Text>

      <InStoreCode inStoreCode={inStoreCode} />
    </>
  ) : null;

  const cta = (
    <Button
      secondary
      title={ctaTitle || `GO TO ${config.APP_NAME.toUpperCase()} HOME`}
      onPress={goToHome}
      // DIS-1842: omit the key when narrow. withTheme merges props over the
      // theme, so `buttonStyle={undefined}` drops LMGButton's secondary fill.
      {...(isWide ? {buttonStyle: styles.ctaWide} : {})}
    />
  );

  // Desktop: the centred card, as the Lite Success screen (no desktop frame;
  // standard from 536:4247). Member card and temporary code in the 343 form
  // column, CTA below them. No AddToWallet: on desktop web it renders an
  // empty, unlabelled button, and a wallet pass is a phone feature.
  if (isWide) {
    return (
      <KeyboardAwareScrollView
        style={{backgroundColor: 'white'}}
        contentContainerStyle={{paddingBottom: tabBarPadding}}
        keyboardShouldPersistTaps="always">
        <SignUpPageLayout
          centred
          isWide={isWide}
          onLayout={onLayout}
          testID="successLoyaltyScreen"
          heading={
            <Text style={[type.heading.h1, lineHeight.heading.h1, styles.headingWide]}>
              {title}
            </Text>
          }
          intro={
            typeof text === 'string' ? (
              <Text style={[type.paragraph.p1, lineHeight.paragraph.p1, styles.centred]}>
                {text}
              </Text>
            ) : (
              text
            )
          }
          main={
            <>
              {memberCard}
              {tempCode}
            </>
          }
          aside={cta}
        />
      </KeyboardAwareScrollView>
    );
  }

  return (
    <KeyboardAwareScrollView
      style={{backgroundColor: 'white'}}
      contentContainerStyle={{paddingBottom: tabBarPadding}}
      keyboardShouldPersistTaps="always">
      <ContentView onLayout={onLayout}>
        <Text variant="heading1">{title}</Text>

        {typeof text === 'string' ? <Text variant="body2">{text}</Text> : text}

        <View style={{gap: 8, marginTop: 24}}>
          {memberCard}
          {/* DIS-1556: Zen only issues the consumer card template, so the wallet
              pass never generates for staff members. Hide the dead button. */}
          {!isStaff && !isLmgStaff && <AddToWallet pending={pending} walletUrl={walletUrl || ''} />}
        </View>

        {tempCode ? (
          <>
            {tempCode}
            <View style={{height: 50}} />
          </>
        ) : null}

        <View style={{gap: space.s8, marginTop: space.s16}}>{cta}</View>
      </ContentView>
    </KeyboardAwareScrollView>
  );
});

export default SuccessLoyalty;

// The narrow heading used to be marginTop 50, off the space scale (DIS-1856).
// 48 is the nearest step. tempBody's 8 is already space.s8.
const styles = StyleSheet.create({
  tempHeading: {
    marginTop: space.s48,
  },
  tempBody: {
    marginTop: 8,
  },
  // Wide only: the centred card spaces the heading and intro itself. The
  // temporary code is a section below the member card, its copy a related gap
  // below its heading, all centred like the rest of the card.
  headingWide: {
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
  },
  centred: {
    textAlign: 'center',
  },
  tempHeadingWide: {
    marginTop: layout.desktop.stackSection,
    textAlign: 'center',
  },
  tempBodyWide: {
    marginTop: layout.desktop.stackRelated,
    textAlign: 'center',
  },
  // Same CTA geometry as the other sign-up CTAs.
  ctaWide: {
    width: '100%',
    height: CTA_HEIGHT,
    borderRadius: CTA_RADIUS,
  },
});
